import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  App as AntdApp,
  Breadcrumb,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  AuditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Show, useSelect } from "@refinedev/antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import { auditPlotService } from "../../services/api";
import type { AuditPlot, AuditPlotFormValues } from "../../types/audit";
import { usePageTitle } from "../../hooks";
import { PlotMapEditor } from "./components/PlotMapEditor";
import { PlantingsTable } from "./components/PlantingsTable";
import { useGetIdentity } from "@refinedev/core";
import type { UserInfo } from "../../types/auth";
import { ADMIN_USER_TYPES } from "../../types/auth";
import "leaflet/dist/leaflet.css";

const { Text, Title } = Typography;

interface ProductionBlock {
  key: string;
  percentage: number;
  avg_yield: number;
}

interface PlotFormFields {
  name: string;
  plot_type: number;
  pattern: number;
  intermediate_wood?: number | null;
  surface: number;
  plantation_date: Dayjs | null;
  plantation_frame_row: number;
  plantation_frame_column: number;
  plants: number;
  grafting?: number | null;
  doubled_with?: string | null;
  is_ilegal: boolean;
  /** Calibre medio de la parcela (mm). Un único valor plot-level;
   *  antes se pedía por bloque. */
  avg_caliber?: number | null;
}

const accentInsensitiveFilter = (
  input: string,
  option?: { label?: unknown },
) => {
  const norm = (s: unknown) =>
    String(s ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");
  return norm(option?.label).includes(norm(input));
};

/**
 * AuditPlotReview — full-page revisión for a precharged AuditPlot.
 *
 * Replaces the legacy 3-step wizard modal: the three sections
 * (Datos, Geometría, Producción) are stacked in a single Card so
 * the auditor sees everything at once, mirroring the style of the
 * "Crear Parcela" form. State + submit logic come from the modal
 * verbatim — only the chrome changes.
 */
export const AuditPlotReview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const { id: auditId, plotId } = useParams<{ id: string; plotId: string }>();
  const [form] = Form.useForm<PlotFormFields>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plot, setPlot] = useState<AuditPlot | null>(null);
  // Edición de plantings desde web disponible para admin (user_type
  // 1, 2) y coordinador (4). Auditor puro (5) edita desde el móvil.
  const { data: identity } = useGetIdentity<UserInfo>();
  const canEditPlantings = (() => {
    const t = identity?.app_user?.user_type;
    if (t == null) return false;
    return (ADMIN_USER_TYPES as readonly number[]).includes(t) || t === 4;
  })();
  // Reload SÓLO el plot (con plantings actualizados) tras un
  // save/delete del editor de plantings. No re-carga geometría ni
  // bloques ni resetea el form — el auditor puede seguir editando
  // el resto sin perder cambios.
  const refreshPlantings = useCallback(async () => {
    if (!plotId) return;
    try {
      const response = await auditPlotService.getPlot(plotId);
      const data = (
        (response as unknown as { properties?: AuditPlot }).properties ??
        (response as unknown as AuditPlot)
      );
      setPlot(data);
    } catch {
      // silent — el editor ya notifica el error
    }
  }, [plotId]);
  const [geometry, setGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [productionBlocks, setProductionBlocks] = useState<ProductionBlock[]>([]);

  usePageTitle(
    plot
      ? `${t("plot.review_title", "Revisión")} — ${plot.name}`
      : t("plot.review_title", "Revisión"),
  );

  // Catalogues for the selects.
  const { selectProps: patternSelectProps } = useSelect({
    resource: "patterns",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });
  const { selectProps: plotTypeSelectProps } = useSelect({
    resource: "plot-types",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });
  const { selectProps: intermediateWoodSelectProps } = useSelect({
    resource: "intermediate-woods",
    optionLabel: "name",
    optionValue: "id",
    pagination: { pageSize: 500 },
  });

  // Load plot + geometry + blocks when the page mounts (or plotId changes).
  useEffect(() => {
    if (!plotId) return;
    let cancelled = false;
    setLoading(true);
    setPlot(null);
    form.resetFields();
    setGeometry(null);
    setProductionBlocks([]);
    // Normalise whatever the backend hands us into a plain GeoJSON
    // Geometry. Accepts a Feature, a JSON string, or null; returns
    // a Geometry (`Polygon` / `MultiPolygon`) or null. The
    // GeoFeatureModelSerializer used by some endpoints wraps the
    // geom inside a Feature, which the area helper doesn't
    // recognise, so we unwrap before storing.
    const normaliseGeometry = (
      raw: unknown,
    ): GeoJSON.Geometry | null => {
      if (raw == null) return null;
      let g: any = raw;
      if (typeof g === "string") {
        try {
          g = JSON.parse(g);
        } catch {
          return null;
        }
      }
      // Unwrap Feature → Geometry. Some endpoints return the plot
      // as a GeoJSON Feature; the area helper only understands the
      // inner Geometry.
      while (g && typeof g === "object" && g.type === "Feature" && g.geometry) {
        g = g.geometry;
      }
      if (g && typeof g === "object" && Array.isArray(g.coordinates)) {
        // Accept any geometry whose `type` ends in "Polygon" — the
        // area helper handles Polygon / MultiPolygon. Anything else
        // (LineString, Point…) wouldn't make sense for a parcela
        // anyway.
        if (g.type === "Polygon" || g.type === "MultiPolygon") {
          return g as GeoJSON.Geometry;
        }
      }
      return null;
    };

    (async () => {
      try {
        const response = await auditPlotService.getPlot(plotId);
        // GeoFeatureModelSerializer returns { type, id, geometry,
        // properties:{...fields} } — pull the data out of properties
        // when present.
        const data = ((response as any).properties || response) as AuditPlot;
        if (cancelled) return;
        setPlot(data);
        form.setFieldsValue({
          name: data.name,
          plot_type: data.plot_type,
          pattern: data.pattern,
          intermediate_wood: data.intermediate_wood ?? undefined,
          surface: data.surface,
          plantation_date: data.plantation_date
            ? dayjs(data.plantation_date)
            : null,
          plantation_frame_row: data.plantation_frame_row,
          plantation_frame_column: data.plantation_frame_column,
          plants: data.plants,
          grafting: data.grafting ?? undefined,
          doubled_with: data.doubled_with ?? undefined,
          is_ilegal: !!data.is_ilegal,
          // Plot-level: calibre medio (mm). Fallback null → undefined
          // para que el InputNumber lo pinte vacío.
          avg_caliber: data.avg_caliber_calculated ?? undefined,
        });

        // Seed the geometry from whichever source returns one first:
        //   1) the plot row itself (`plot.geom` or the top-level
        //      `geometry` from the Feature wrapper);
        //   2) the dedicated `/audit-plots/{id}/geometry/` endpoint,
        //      which can also return a similar-plot fallback.
        // Using both means the area helper has something to chew on
        // from the first render — the user shouldn't have to redraw
        // just to see the polygon area.
        const fromPlot =
          normaliseGeometry((data as any).geom) ??
          normaliseGeometry((response as any).geometry);
        if (fromPlot) {
          setGeometry(fromPlot);
        }
        try {
          const geometryData = await auditPlotService.getPlotGeometry(plotId);
          if (cancelled) return;
          const fromEndpoint = normaliseGeometry(geometryData.geom);
          if (fromEndpoint) {
            setGeometry(fromEndpoint);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[PlotReview] geometry endpoint failed", e);
        }
        if (data.blocks && data.blocks.length > 0) {
          setProductionBlocks(
            data.blocks.map((b, idx) => ({
              key: `existing-${idx}`,
              percentage: b.percentage || 0,
              avg_yield: b.avg_yield || 0,
            })),
          );
        }
      } catch {
        message.error(
          t("plot.review_load_error", "No se pudo cargar la parcela"),
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plotId, form, message, t]);

  // --- Geometry-driven helpers ---------------------------------------
  // Watched form fields used by the auto-calculations below. Surface
  // is in ha; marco_row / marco_column are planting frame in metres.
  const watchedSurface = Form.useWatch("surface", form);
  const watchedRow = Form.useWatch("plantation_frame_row", form);
  const watchedColumn = Form.useWatch("plantation_frame_column", form);

  // Spherical polygon area in hectares from a GeoJSON geometry
  // (EPSG:4326). Same formula used by plot-form.tsx — sub-percent
  // error vs. the geodetic ground truth for parcel-scale shapes.
  const polygonAreaHa = useMemo(() => {
    if (!geometry) return null;
    const coordsList: number[][][] = [];
    if (geometry.type === "Polygon") {
      coordsList.push(geometry.coordinates[0] as unknown as number[][]);
    } else if (geometry.type === "MultiPolygon") {
      for (const poly of geometry.coordinates as any[]) {
        coordsList.push(poly[0]);
      }
    } else {
      return null;
    }
    const R = 6378137; // WGS84 equatorial radius (m)
    const toRad = (d: number) => (d * Math.PI) / 180;
    let totalM2 = 0;
    for (const ring of coordsList) {
      let area = 0;
      for (let i = 0; i < ring.length - 1; i++) {
        const [lon1, lat1] = ring[i];
        const [lon2, lat2] = ring[i + 1];
        area +=
          (toRad(lon2) - toRad(lon1)) *
          (2 + Math.sin(toRad(lat1)) + Math.sin(toRad(lat2)));
      }
      totalM2 += Math.abs((area * R * R) / 2);
    }
    return totalM2 / 10000;
  }, [geometry]);

  // Auto-compute the number of plants whenever surface or marco
  // changes. Formula: `plants = surface_ha * 10000 / (row_m *
  // column_m)`. The user can override the value manually, but any
  // later edit to surface or marco will overwrite it again —
  // intentional "set it and forget it" behaviour, same as the
  // create-plot form.
  useEffect(() => {
    const s = Number(watchedSurface);
    const r = Number(watchedRow);
    const c = Number(watchedColumn);
    if (!s || !r || !c) return;
    const plants = Math.round((s * 10000) / (r * c));
    if (Number.isFinite(plants) && plants > 0) {
      form.setFieldValue("plants", plants);
    }
  }, [watchedSurface, watchedRow, watchedColumn, form]);

  // --- Production helpers --------------------------------------------
  const totalPercentage = useMemo(
    () => productionBlocks.reduce((s, b) => s + (b.percentage || 0), 0),
    [productionBlocks],
  );
  const estimatedProduction = useMemo(() => {
    const plants = Number(form.getFieldValue("plants") ?? plot?.plants ?? 0);
    if (!plants || productionBlocks.length === 0) return 0;
    const totalYield = productionBlocks.reduce(
      (sum, b) => sum + b.percentage * b.avg_yield,
      0,
    );
    return Math.round((totalYield / 100) * plants);
  }, [productionBlocks, plot, form]);

  const addProductionBlock = () =>
    setProductionBlocks((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        percentage: 0,
        avg_yield: 0,
      },
    ]);

  const removeProductionBlock = (key: string) =>
    setProductionBlocks((prev) => prev.filter((b) => b.key !== key));

  const updateProductionBlock = (
    key: string,
    field: keyof ProductionBlock,
    value: number,
  ) => {
    if (field === "percentage") {
      const othersTotal = productionBlocks
        .filter((b) => b.key !== key)
        .reduce((s, b) => s + (b.percentage || 0), 0);
      if (othersTotal + value > 100) {
        message.warning(
          t(
            "plot.percentage_exceeded",
            "La suma de los porcentajes no puede superar el 100%",
          ),
        );
        return;
      }
    }
    setProductionBlocks((prev) =>
      prev.map((b) => (b.key === key ? { ...b, [field]: value } : b)),
    );
  };

  // --- Save -----------------------------------------------------------
  const submitReview = async () => {
    if (!plotId) return;
    let values: PlotFormFields;
    try {
      values = await form.validateFields();
    } catch {
      // Scroll the user to whichever required field tripped validation.
      form.scrollToField(Object.keys(form.getFieldsValue())[0]);
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<AuditPlotFormValues> = {
        name: values.name,
        plot_type: values.plot_type,
        pattern: values.pattern,
        intermediate_wood: values.intermediate_wood ?? null,
        surface: values.surface,
        plantation_date: values.plantation_date
          ? values.plantation_date.format("YYYY-MM-DD")
          : null,
        plantation_frame_row: values.plantation_frame_row,
        plantation_frame_column: values.plantation_frame_column,
        plants: values.plants,
        grafting: values.grafting ?? null,
        doubled_with: values.doubled_with ?? null,
        is_ilegal: values.is_ilegal,
        kg_plot: estimatedProduction,
        // Calibre plot-level (mm). Backend lo persiste en
        // `avg_caliber_calculated` (mismo campo del modelo). El
        // Form.Item usa el name corto `avg_caliber` en la UI, pero al
        // enviarlo hay que mapearlo a la clave real del serializer —
        // si no, DRF lo descarta y el cambio se pierde.
        avg_caliber_calculated:
          values.avg_caliber != null && Number.isFinite(values.avg_caliber)
            ? Number(values.avg_caliber)
            : 0,
        blocks: productionBlocks.map((b) => ({
          percentage: b.percentage,
          avg_yield: b.avg_yield,
          // Backend legacy: seguimos enviando avg_caliber=0 para no
          // dejar el campo NOT NULL a null. La UI ya no lo captura;
          // el ponderado plot-level manda.
          avg_caliber: 0,
        })),
        geom: geometry,
      };
      await auditPlotService.updatePlot(plotId, payload);
      message.success(t("plot.review_saved", "Revisión guardada"));
      navigate(`/audits/${auditId}`);
    } catch (e: any) {
      message.error(
        e?.message ||
          t("plot.review_save_error", "No se pudo guardar la revisión"),
      );
    } finally {
      setSaving(false);
    }
  };

  // --- Render --------------------------------------------------------
  const breadcrumb = (
    <Breadcrumb
      items={[
        {
          title: (
            <Link to="/">
              <HomeOutlined /> {t("home.title", "Inicio")}
            </Link>
          ),
        },
        {
          title: (
            <Link to="/audits">
              <AuditOutlined /> {t("audit.title", "Auditorías")}
            </Link>
          ),
        },
        {
          title: (
            <Link to={`/audits/${auditId}`}>
              {auditId}
            </Link>
          ),
        },
        {
          title: (
            <>
              <EnvironmentOutlined />{" "}
              {plot
                ? `${t("plot.review_title", "Revisión")} — ${plot.name}`
                : t("plot.review_title", "Revisión")}
            </>
          ),
        },
      ]}
    />
  );

  return (
    <Show
      isLoading={false}
      title={
        plot
          ? `${t("plot.review_title", "Revisión")} — ${plot.name}`
          : t("plot.review_title", "Revisión")
      }
      breadcrumb={breadcrumb}
      headerButtons={() => (
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(`/audits/${auditId}`)}
            disabled={saving}
          >
            {t("common.back", "Volver")}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={submitReview}
          >
            {t("plot.review_save", "Guardar revisión")}
          </Button>
        </Space>
      )}
    >
      <Card loading={loading}>
        <Spin spinning={loading}>
          {/* -------- DATOS -------- */}
          <Divider orientation="left" style={{ marginTop: 0 }}>
            {t("plot.review_step_data", "Datos")}
          </Divider>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("plot.name", "Nombre")}
                  name="name"
                  rules={[{ required: true }]}
                >
                  <Input
                    placeholder={t("plot.name_placeholder", "Ej. I21") as string}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label={t("plot.surface", "Superficie (ha)")}
                  name="surface"
                  rules={[{ required: true }]}
                  extra={
                    polygonAreaHa != null ? (
                      <span>
                        {t("plot.polygon_area_hint", "Polígono dibujado")}:{" "}
                        <strong>{polygonAreaHa.toFixed(2)} ha</strong>{" "}
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, height: "auto" }}
                          onClick={() =>
                            form.setFieldValue(
                              "surface",
                              Number(polygonAreaHa.toFixed(2)),
                            )
                          }
                        >
                          {t("plot.apply_polygon_area", "Usar este valor")}
                        </Button>
                      </span>
                    ) : undefined
                  }
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.01}
                    decimalSeparator=","
                    placeholder="0,00"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.plot_type", "Tipo de parcela")}
                  name="plot_type"
                  rules={[{ required: true }]}
                >
                  <Select
                    {...plotTypeSelectProps}
                    placeholder={
                      t("plot.plot_type_placeholder", "Selecciona el tipo") as string
                    }
                    showSearch
                    filterOption={accentInsensitiveFilter}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.pattern", "Patrón")}
                  name="pattern"
                  rules={[{ required: true }]}
                >
                  <Select
                    {...patternSelectProps}
                    placeholder={
                      t("plot.pattern_placeholder", "Selecciona el patrón") as string
                    }
                    showSearch
                    filterOption={accentInsensitiveFilter}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.intermediate_wood", "Intermediario")}
                  name="intermediate_wood"
                >
                  <Select
                    {...intermediateWoodSelectProps}
                    placeholder={
                      t(
                        "plot.intermediate_wood_placeholder",
                        "Selecciona la madera intermedia",
                      ) as string
                    }
                    allowClear
                    showSearch
                    filterOption={accentInsensitiveFilter}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.plantation_date", "Fecha plantación")}
                  name="plantation_date"
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    allowClear
                    placeholder={
                      t(
                        "plot.plantation_date_placeholder",
                        "DD/MM/AAAA",
                      ) as string
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.plants", "Número de plantas")}
                  name="plants"
                  rules={[{ required: true }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.grafting", "Injertos")}
                  name="grafting"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="0"
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={12} md={6}>
                <Form.Item
                  label={t("plot.frame_row", "Marco (fila)")}
                  name="plantation_frame_row"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.1}
                    placeholder="6,0"
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={6}>
                <Form.Item
                  label={t("plot.frame_column", "Marco (columna)")}
                  name="plantation_frame_column"
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.1}
                    placeholder="4,0"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={t("plot.doubled_with", "Doblado con")}
                  name="doubled_with"
                >
                  <Input
                    allowClear
                    placeholder={
                      t(
                        "plot.doubled_with_placeholder",
                        "Variedad con la que está doblada",
                      ) as string
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item
                  label={t("plot.is_ilegal", "Ilegal")}
                  name="is_ilegal"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </Form>

          {/* -------- GEOMETRÍA -------- */}
          <Divider orientation="left">
            {t("plot.review_step_geometry", "Geometría")}
          </Divider>
          <Text type="secondary">
            {t(
              "plot.review_geometry_help",
              "Revisa la geometría de la parcela. Si ha cambiado respecto a la última auditoría, redibújala.",
            )}
          </Text>
          <div style={{ height: 680, marginTop: 12 }}>
            <PlotMapEditor
              geometry={geometry as any}
              plotName={plot?.name ?? ""}
              onGeometryChange={(g: any) => setGeometry(g)}
            />
          </div>

          {/* -------- PLANTACIONES -------- */}
          {plot?.plantings && (plot.plantings.length >= 2 || canEditPlantings) ? (
            <>
              <Divider orientation="left">
                Plantaciones{" "}
                {plot.plantings.length >= 2 ? (
                  <Tag color="green" style={{ marginLeft: 4 }}>
                    D×{plot.plantings.length}
                  </Tag>
                ) : null}
              </Divider>
              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                {plot.plantings.length >= 2
                  ? "Esta parcela está doblada. Las plantaciones abajo conviven en la misma parcela con distinta variedad/marco/plantas."
                  : "La parcela tiene una única plantación. Puedes añadir más si es doblada."}
              </Text>
              <PlantingsTable
                plantings={plot.plantings}
                editable={canEditPlantings}
                auditPlotId={plot.id}
                onChange={refreshPlantings}
              />
            </>
          ) : null}

          {/* -------- PRODUCCIÓN -------- */}
          <Divider orientation="left">
            {t("plot.review_step_production", "Producción")}
          </Divider>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Text type="secondary">
              {t(
                "plot.review_production_help",
                "Distribuye la producción esperada en bloques. Cada bloque combina un porcentaje de la parcela con su rendimiento medio (kg/planta). El calibre medio se pide una sola vez para toda la parcela.",
              )}
            </Text>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={addProductionBlock}
                disabled={totalPercentage >= 100}
              >
                {t("plot.add_block", "Añadir bloque")}
              </Button>
              <Text>
                {t("plot.percentage_total", "Porcentaje total")}:{" "}
                <strong>{totalPercentage}%</strong>
              </Text>
            </Space>
            <Table
              dataSource={productionBlocks}
              rowKey="key"
              pagination={false}
              size="small"
              locale={{
                emptyText: t(
                  "plot.blocks_empty",
                  "Sin bloques de aforo. Añade uno para comenzar.",
                ),
              }}
              columns={[
                {
                  title: t("plot.percentage", "%"),
                  dataIndex: "percentage",
                  width: 110,
                  render: (_: number, row: ProductionBlock) => (
                    <InputNumber
                      value={row.percentage}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(v) =>
                        updateProductionBlock(
                          row.key,
                          "percentage",
                          Number(v ?? 0),
                        )
                      }
                    />
                  ),
                },
                {
                  title: t("plot.avg_yield", "Rdto. medio (kg/pl)"),
                  dataIndex: "avg_yield",
                  render: (_: number, row: ProductionBlock) => (
                    <InputNumber
                      value={row.avg_yield}
                      min={0}
                      step={0.1}
                      decimalSeparator=","
                      onChange={(v) =>
                        updateProductionBlock(
                          row.key,
                          "avg_yield",
                          Number(v ?? 0),
                        )
                      }
                    />
                  ),
                },
                {
                  title: "",
                  dataIndex: "key",
                  width: 48,
                  render: (_: string, row: ProductionBlock) => (
                    <Button
                      danger
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeProductionBlock(row.key)}
                    />
                  ),
                },
              ]}
            />
            {/* Calibre medio plot-level (mm). Único input para toda la
                parcela. Reemplaza la antigua columna "Calibre medio"
                que existía por bloque. */}
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label={t("plot.avg_caliber_plot", "Calibre medio (mm)")}
                  name="avg_caliber"
                  tooltip={t(
                    "plot.avg_caliber_plot_help",
                    "Se mide una vez para toda la parcela, no por bloque.",
                  )}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    step={0.1}
                    decimalSeparator=","
                    placeholder={t(
                      "plot.avg_caliber_placeholder",
                      "Ej: 65",
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider style={{ margin: "8px 0" }} />
            <Title level={5} style={{ margin: 0 }}>
              {t("plot.estimated_production", "Producción estimada")}:{" "}
              <Text type="success">
                {estimatedProduction.toLocaleString("es-ES")} kg
              </Text>
            </Title>
          </Space>

          {/* Footer actions — repeated at the bottom so the user
              doesn't have to scroll back up after a long Producción
              section. */}
          <Divider />
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              onClick={() => navigate(`/audits/${auditId}`)}
              disabled={saving}
            >
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              onClick={submitReview}
            >
              {t("plot.review_save", "Guardar revisión")}
            </Button>
          </Space>
        </Spin>
      </Card>
    </Show>
  );
};

export default AuditPlotReview;
